// src/utils/api.js
import { API_ORIGIN } from "./env";
import { cfLoginUrl } from "./cfAccess";

/** API base 결정(프로덕션은 상대경로, 로컬만 절대경로) */
function baseForApi() {
  const abs = (API_ORIGIN || "").trim();
  if (!abs) return "";
  try {
    const u = new URL(abs);
    if (u.origin === window.location.origin) return "";
    if (!/^https?:$/i.test(u.protocol)) return "";
    return u.origin;
  } catch {
    return "";
  }
}

/** 공통 fetch(JSON) 래퍼 */
async function fetchJSON(path, options = {}) {
  const base = baseForApi();
  const url = `${base}${path}`;
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.headers || {}),
    },
  });

  // 인증 필요 시 CF Access 로그인으로 보냄
  if (res.status === 401 || res.status === 403) {
    const returnTo = window.location.href;
    window.location.href = cfLoginUrl(returnTo);
    throw new Error("AUTH_REQUIRED");
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} – ${text}`);
  }

  if (res.status === 204) return {};
  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return {};
  return res.json();
}

/* ===== 목록 ===== */
export const getStaff = async (deleted = false, opts = {}) => {
  const d = await fetchJSON(`/api/staff${deleted ? "?deleted=true" : ""}`, opts);
  return Array.isArray(d) ? d : [];
};

export const getEvents = async (deleted = false, opts = {}) => {
  const d = await fetchJSON(`/api/events${deleted ? "?deleted=true" : ""}`, opts);
  return Array.isArray(d) ? d : [];
};

/* 공통: 숫자 배열 정규화 */
const asNumericArray = (ids = []) => (ids || []).map(Number).filter(Number.isFinite);

/* ===== 이벤트 삭제/복원 ===== */
// 1순위: /soft-delete, 404면 구버전 /delete 로 폴백
export const deleteEvents = async (ids = [], opts = {}) => {
  const body = JSON.stringify(asNumericArray(ids));
  try {
    return await fetchJSON(`/api/events/soft-delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      ...opts,
    });
  } catch (e) {
    if (String(e.message).startsWith("HTTP 404")) {
      return await fetchJSON(`/api/events/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        ...opts,
      });
    }
    throw e;
  }
};

export const restoreEvents = (ids = [], opts = {}) =>
  fetchJSON(`/api/events/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(asNumericArray(ids)),
    ...opts,
  });

/* ===== 스태프 삭제/복원/영구 ===== */
export const softDeleteStaff = (ids = [], opts = {}) =>
  fetchJSON(`/api/staff/soft-delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(asNumericArray(ids)),
    ...opts,
  });

export const restoreStaff = (ids = [], opts = {}) =>
  fetchJSON(`/api/staff/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(asNumericArray(ids)),
    ...opts,
  });

export const hardDeleteStaff = (ids = [], opts = {}) =>
  fetchJSON(`/api/staff/hard-delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(asNumericArray(ids)),
    ...opts,
  });

/* ===== 파일 다운로드 (엑셀 등, 헤더/쿠키 포함) ===== */
function parseFilenameFromCD(cd = "") {
  // filename*=UTF-8''... 또는 filename="..."
  const star = cd.match(/filename\*\s*=\s*[^']*''([^;]+)/i);
  if (star) return decodeURIComponent(star[1]);
  const normal = cd.match(/filename\s*=\s*"([^"]+)"/i);
  if (normal) return normal[1];
  return "";
}

/**
 * 서버에서 파일을 받아서 저장. (CF Access 쿠키 포함)
 * - path: /api/... 형태
 * - fallbackName: Content-Disposition 없을 때 쓸 이름
 */
export async function downloadFile(path, fallbackName = "download.bin") {
  const base = baseForApi();
  const url = `${base}${path}`;

  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "*/*" },
  });

  // 인증 오류면 Access 로그인
  if (res.status === 401 || res.status === 403) {
    const returnTo = window.location.href;
    window.location.href = cfLoginUrl(returnTo);
    throw new Error("AUTH_REQUIRED");
  }

  // 네트워크/HTTP 오류
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} – ${txt}`);
  }

  // ✅ 핵심: 엑셀이 아닌 HTML/텍스트(로그인·에러 페이지)를 차단
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  const looksLikeHtml = ct.includes("text/html") || ct.includes("text/plain");
  const looksLikeAccess = /cdn-cgi\/access|cloudflare|cf-/.test((res.url || "").toLowerCase());
  if (looksLikeHtml || looksLikeAccess) {
    const returnTo = window.location.href;
    try { await res.text(); } catch {}
    window.location.href = cfLoginUrl(returnTo);
    throw new Error("AUTH_REQUIRED");
  }

  const cd = res.headers.get("content-disposition") || "";
  const name = parseFilenameFromCD(cd) || fallbackName;

  const blob = await res.blob();
  const obj = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = obj;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(obj);
  }, 0);
}

/** 프런트/백 분리 배포에서도 안전한 절대 API URL 생성 */
export function apiUrl(path = "") {
  const base = baseForApi();
  return `${base}${path}`;
}
